import {
    createCurrencyFormatter,
    createDateFormatter,
    createNumberFormatter,
    createOptionsFormatter,
    type DataTableColumn,
} from '@/components/ui/data-table-customized'
import {
    Product,
    ProductConfigOption,
    ProductGroupWithProducts,
    ProductPricingStep,
} from '@/modules/Settings/types/product'

/** Group all products by product_group_name */
export const groupByProductGroup = (
    products: Product[]
): ProductGroupWithProducts[] => {
    const map = new Map<string, ProductGroupWithProducts>()
    for (const p of products) {
        const key = p.product_group_name
        if (!map.has(key)) {
            map.set(key, {
                product_group_id: p.product_group_id,
                product_group_name: p.product_group_name,
                product_group_code: '',
                products: [],
            })
        }
        map.get(key)!.products.push(p)
    }
    return Array.from(map.values())
}

export const getGroupColumns = (
    t: (key: string) => string
): DataTableColumn<ProductGroupWithProducts>[] => [
    {
        key: 'product_group_name',
        title: t('product-group-name'),
        width: 300,
    },
    {
        key: 'products' as any,
        title: t('product-count'),
        width: 130,
        cellClassName: 'text-right',
        valueGetter: (row) =>
            row.products?.filter((p) => !p.is_product_addon).length || 0,
        formatter: createNumberFormatter({ decimals: 0 }),
    },
    {
        key: 'addon_count' as any,
        title: t('addon-count'),
        width: 130,
        cellClassName: 'text-right',
        valueGetter: (row) =>
            row.products?.filter((p) => p.is_product_addon).length || 0,
        formatter: createNumberFormatter({ decimals: 0 }),
    },
]

export const getProductColumns = (
    t: (key: string) => string
): DataTableColumn<Product>[] => [
    {
        key: 'product_id',
        title: 'ID',
        width: 70,
    },
    {
        key: 'product_code',
        title: t('product-code'),
        width: 150,
    },
    {
        key: 'product_name',
        title: t('product-name'),
        width: 280,
    },
    {
        key: 'product_description',
        title: t('description'),
        width: 200,
        valueGetter: (row) => row.product_description || '-',
    },
    {
        key: 'product_price_model',
        title: t('price-model'),
        width: 120,
        valueGetter: (row) => row.product_price_model || 'flexible',
        formatter: createOptionsFormatter({
            options: [
                {
                    label: t('price-model-flexible'),
                    value: 'flexible',
                    color: 'success',
                },
                { label: 'Package', value: 'package', color: 'info' },
                { label: 'Bundle', value: 'bundle', color: 'info' },
                {
                    label: '% Parent',
                    value: 'percent_of_parent',
                    color: 'warning',
                },
            ],
            renderMode: 'chip',
        }),
    },
    {
        key: 'current_price',
        title: t('price'),
        width: 140,
        cellClassName: 'text-right',
        formatter: createCurrencyFormatter({ currency: '' }),
    },
    {
        key: 'product_hidden',
        title: t('status'),
        width: 100,
        valueGetter: (row) => !row.product_hidden,
        formatter: createOptionsFormatter({
            options: [
                { label: t('active'), value: true, color: 'success' },
                { label: t('hidden'), value: false, color: 'warning' },
            ],
            renderMode: 'chip',
            textTransform: 'capitalize',
        }),
    },
    {
        key: 'created_at',
        title: t('created-at'),
        width: 130,
        formatter: createDateFormatter({ format: 'DD/MM/YYYY' }),
    },
]

export const getConfigOptionColumns = (
    t: (key: string) => string
): DataTableColumn<ProductConfigOption>[] => [
    {
        key: 'name',
        title: t('option-name'),
        width: 180,
        cellClassName: 'capitalize',
        valueGetter: (row) => t(row.name),
    },
    {
        key: 'type',
        title: t('option-type'),
        width: 100,
        valueGetter: (row) => t(row.type),
    },
    {
        key: 'value',
        title: t('option-value'),
        width: 100,
    },
    {
        key: 'unit',
        title: t('option-unit'),
        width: 80,
        valueGetter: (row) => row.unit || '-',
    },
    {
        key: 'price',
        title: t('option-price'),
        width: 130,
        cellClassName: 'text-right',
        formatter: createCurrencyFormatter({ currency: '' }),
    },
    {
        key: 'is_editable',
        title: t('editable'),
        width: 80,
        valueGetter: (row) => row.is_editable,
        formatter: createOptionsFormatter({
            options: [
                { label: t('yes'), value: true, color: 'success' },
                { label: t('no'), value: false, color: 'error' },
            ],
            renderMode: 'chip',
        }),
    },
]

export const getPricingStepColumns = (
    t: (key: string) => string
): DataTableColumn<ProductPricingStep>[] => [
    {
        key: 'price_start',
        title: t('price-start'),
        width: 130,
        cellClassName: 'text-right',
        formatter: createNumberFormatter({ decimals: 0 }),
    },
    {
        key: 'price_end',
        title: t('price-end'),
        width: 130,
        cellClassName: 'text-right',
        formatter: createNumberFormatter({ decimals: 0 }),
    },
    {
        key: 'price_per_unit',
        title: t('price-per-unit'),
        width: 130,
        cellClassName: 'text-right',
        formatter: createCurrencyFormatter({ currency: '' }),
    },
]
